import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Link } from 'react-router-dom';

interface SurveyResponses {
  email: string;
  institution?: string;
  role?: string;
  peptideInterest: string[];
}

const PEPTIDE_OPTIONS = [
  'BPC-157',
  'TB-500',
  'GLP-1 / Semaglutide',
  'GHK-Cu',
  'Ipamorelin',
  'CJC-1295',
  'Thymosin Alpha 1',
  'Other',
];

const PeptideSurvey: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const [responses, setResponses] = useState<SurveyResponses>({
    email: '',
    institution: '',
    role: '',
    peptideInterest: [],
  });

  const updateResponse = <K extends keyof SurveyResponses>(key: K, value: SurveyResponses[K]) => {
    setResponses((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      await addDoc(collection(db, 'peptide_interest'), {
        email: responses.email,
        institution: responses.institution || null,
        role: responses.role || null,
        peptideInterest: responses.peptideInterest,
        source: 'research_catalogue_request',
        createdAt: new Date(),
      });
      navigate('/survey/thank-you', { state: { email: responses.email } });
    } catch (err: any) {
      setError(err.message || 'Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = () => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(responses.email) && responses.peptideInterest.length > 0;
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        backgroundColor: 'var(--melio-cream)',
        color: 'var(--melio-charcoal)',
        fontFamily: "var(--melio-sans)",
      }}
    >
      <header className="px-6 py-8 md:px-12">
        <div className="max-w-2xl mx-auto flex justify-between items-center">
          <Link
            to="/"
            className="text-lg font-medium tracking-tight flex items-center gap-2"
            style={{ fontFamily: "var(--melio-display)" }}
          >
            <svg width="24" height="24" viewBox="0 0 64 64" fill="none" className="shrink-0">
              <rect width="64" height="64" rx="8" fill="currentColor" />
              <path d="M16 48V20l11 15 11-15v28h-5V35L32 45 21 35v13h-5z" fill="var(--melio-cream)" />
            </svg>
            Melio Therapeutics
          </Link>
        </div>
      </header>

      <main className="flex-1 px-6 md:px-12 pb-24">
        <div className="max-w-xl mx-auto">
          <h2
            className="text-2xl md:text-3xl font-normal mb-2"
            style={{ fontFamily: "var(--melio-display)" }}
          >
            Request research peptide catalogue
          </h2>
          <p className="text-sm mb-8" style={{ color: 'var(--melio-text-muted)' }}>
            We&apos;ll send our catalogue and pricing. All products are for laboratory research purposes only.
          </p>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--melio-charcoal)' }}>
                Email address *
              </label>
              <input
                type="email"
                value={responses.email}
                onChange={(e) => updateResponse('email', e.target.value)}
                placeholder="you@institution.ac.uk"
                className="w-full px-4 py-3 border border-2 focus:outline-none focus:border-[var(--melio-sage)]"
                style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--melio-cream)' }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--melio-charcoal)' }}>
                Institution or organisation (optional)
              </label>
              <input
                type="text"
                value={responses.institution || ''}
                onChange={(e) => updateResponse('institution', e.target.value)}
                placeholder="e.g. University of London, Lab Name"
                className="w-full px-4 py-3 border border-2 focus:outline-none focus:border-[var(--melio-sage)]"
                style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--melio-cream)' }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--melio-charcoal)' }}>
                Role (optional)
              </label>
              <input
                type="text"
                value={responses.role || ''}
                onChange={(e) => updateResponse('role', e.target.value)}
                placeholder="e.g. Researcher, PhD student, Lab manager"
                className="w-full px-4 py-3 border border-2 focus:outline-none focus:border-[var(--melio-sage)]"
                style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--melio-cream)' }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-3" style={{ color: 'var(--melio-charcoal)' }}>
                Peptides of interest * (select all that apply)
              </label>
              <div className="flex flex-col gap-2">
                {PEPTIDE_OPTIONS.map((opt) => (
                  <label key={opt} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={responses.peptideInterest.includes(opt)}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...responses.peptideInterest, opt]
                          : responses.peptideInterest.filter((x) => x !== opt);
                        updateResponse('peptideInterest', next);
                      }}
                    />
                    <span>{opt}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {error && (
            <p className="mt-4 text-sm" style={{ color: '#c53030' }}>
              {error}
            </p>
          )}

          <div className="mt-10">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit() || isSubmitting}
              className="px-8 py-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: canSubmit() ? 'var(--melio-charcoal)' : 'var(--melio-stone)',
                color: 'var(--melio-cream)',
              }}
            >
              {isSubmitting ? 'Submitting...' : 'Request catalogue'}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PeptideSurvey;
